// @flow

import React, { useMemo } from 'react';
import { connect } from 'react-redux';

import CategorizedFilterBar from '../pills/categorized-filter-bar';
import type { Filter } from '../pills/categorized-filter-bar';
import type {
  CategoryMeta,
  Option,
} from '../pills/categorized-picker-popover';
import { categorizedFilterPredicate } from '../pills/categorized-filter-predicate';

import {
  PREFERENCE_TYPES,
  findPreferenceTypeMeta,
} from './preference-types';

import * as Selectors from '../../selectors';
import * as UserListModel from '../../models/user-list';

export type ActivePreferenceFilter = Filter;

type OwnProps = {
  activeFilters: $ReadOnlyArray<ActivePreferenceFilter>,
  onAdd: (filter: ActivePreferenceFilter) => mixed,
  onRemove: (filter: ActivePreferenceFilter) => mixed,
  onClear: () => mixed,
};

type StateProps = {
  locations: Object[],
  entityTags: Object,
};

type Props = OwnProps & StateProps;

const FALLBACK_CATEGORY_COLOR = '#5c7080';

const PreferencesFilterBar = (props: Props) => {
  const {
    activeFilters,
    onAdd,
    onRemove,
    onClear,
    locations,
    entityTags,
  } = props;

  const deps = useMemo(
    () => ({ locations, entityTags }),
    [locations, entityTags],
  );

  const categories: $ReadOnlyArray<CategoryMeta> = useMemo(
    () =>
      PREFERENCE_TYPES.map((meta) => ({
        key: meta.key,
        label: meta.label,
        color: meta.color || FALLBACK_CATEGORY_COLOR,
      })),
    [],
  );

  const options: $ReadOnlyArray<Option> = useMemo(
    () =>
      PREFERENCE_TYPES.flatMap((meta) =>
        meta.getOptions(deps).map((o) => ({
          id: o.id,
          label: o.label,
          categoryKey: meta.key,
          subTypeKey: o.subTypeKey,
          subTypeLabel: o.subTypeLabel,
          color: o.color,
        })),
      ),
    [deps],
  );

  const handlePick = (option: Option) => {
    const meta = findPreferenceTypeMeta(option.categoryKey);
    const color =
      option.color || (meta && meta.color) || FALLBACK_CATEGORY_COLOR;
    onAdd({
      id: option.id,
      categoryKey: option.categoryKey,
      valueLabel: option.label,
      color,
    });
  };

  return (
    <CategorizedFilterBar
      label="Preferences:"
      categories={categories}
      options={options}
      activeFilters={activeFilters}
      onAdd={handlePick}
      onRemove={onRemove}
      onClear={onClear}
      categoryHeaderLabel="Filter by preference type"
    />
  );
};

const mapStateToProps = (state): StateProps => ({
  locations: Selectors.getLocations(state),
  entityTags: Selectors.getEntityTagsState(state),
});

export const userPreferenceFilterPredicate = (
  filters: $ReadOnlyArray<ActivePreferenceFilter>,
): ((user: UserListModel.userT) => boolean) =>
  (categorizedFilterPredicate(filters, (user, categoryKey) => {
    const meta = findPreferenceTypeMeta(categoryKey);
    return meta ? meta.getUserValuesForType(user) : [];
  }): any);

export default connect(mapStateToProps)(PreferencesFilterBar);
